import clsx from 'clsx';
import { func, object } from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from 'components';
import { useUser } from 'hooks';

const ParticipantCard = ({ participant, openModal, onRemoveParticipant }) => {
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const isCurrentUser = participant?.serialNumber === currentUser?.serialNumber;
  const cardFilled = !!participant.name && !!participant.firstName && !!participant.birthDate;
  const cardEmpty = !participant.name && !participant.firstName && !participant.birthDate;

  const cardBorderClassName = useMemo(() => {
    switch (participant.passengerCategory.label) {
      case 'Adulte':
        return 'border-primary-500';
      case 'Enfant':
      case 'Grand enfant':
        return 'border-secondary-500';
      case 'Bébé':
        return 'border-yellow-500';
      case 'Sénior':
        return 'border-success-500';
      default:
        return 'border-primary-500';
    }
  }, [participant?.passengerCategory?.label]);

  return (
    <div
      className={clsx(
        'p-4 rounded-lg border col-span-1 relative flex flex-col justify-between h-28',
        !isCurrentUser && cardEmpty && 'cursor-pointer hover:shadow-lg hover:shadow-gray-200',
        cardBorderClassName
      )}
    >
      {/*
        CSS Hidden button to trigger action on click on the card only if no participant has been assigned to the card.
        Current user is set by default as the first card in the list and can not be removed or modified.
      */}
      {!isCurrentUser && cardEmpty && (
        <button type="button" className="absolute inset-0 z-10" onClick={openModal}>
          <span className="sr-only">{t('purchase:configuration.openParticipantList')}</span>
        </button>
      )}

      {/* Button to delete participant information. Current user infos can not be removed */}
      {!isCurrentUser && cardFilled && (
        <button type="button" className="absolute z-20 top-2 right-2" onClick={onRemoveParticipant}>
          <Icon name="trash" classes="w-4 h-4 text-gray-500 hover:text-gray-700" />
        </button>
      )}

      <div
        className={clsx(
          'flex flex-wrap justify-between gap-x-1 font-bold mb-4 text-sm',
          !isCurrentUser && cardFilled && 'pr-5'
        )}
      >
        <span className="truncate">{participant.name || t('purchase:configuration.lastName')}</span>
        <span className="truncate">{participant.firstName || t('purchase:configuration.firstName')}</span>
      </div>

      <div className="flex flex-wrap justify-between gap-x-2">
        <span>{participant.birthDate || t('purchase:configuration.birthDate')}</span>
        <span>{participant.passengerCategory.label}</span>
      </div>
    </div>
  );
};

ParticipantCard.propTypes = {
  participant: object,
  openModal: func,
  onRemoveParticipant: func
};

export default ParticipantCard;
